import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/App";
import { Shield, Star, MapPin, CheckCircle, Clock, UserPlus, Eye, MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { useNavigate } from "react-router-dom";

export default function PIOManagement() {
  const { API } = useAuth();
  const navigate = useNavigate();
  const [pios, setPios] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [properties, setProperties] = useState([]);
  const [showAssign, setShowAssign] = useState(false);
  const [selectedPio, setSelectedPio] = useState(null);
  const [showPioDetail, setShowPioDetail] = useState(false);
  const [form, setForm] = useState({ property_id: "", scope: "full_monitoring", notes: "" });
  const [activeTab, setActiveTab] = useState("available");

  const fetchData = useCallback(async () => {
    try {
      const [piRes, aRes, pRes] = await Promise.all([
        fetch(`${API}/pios/available`, { credentials: "include" }),
        fetch(`${API}/pios/assignments`, { credentials: "include" }),
        fetch(`${API}/properties`, { credentials: "include" }),
      ]);
      if (piRes.ok) setPios(await piRes.json());
      if (aRes.ok) setAssignments(await aRes.json());
      if (pRes.ok) setProperties(await pRes.json());
    } catch (e) { console.error(e); }
  }, [API]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleAssign = async () => {
    if (!selectedPio || !form.property_id) return;
    try {
      const res = await fetch(`${API}/pios/assign`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ pio_id: selectedPio.pio_id, ...form }),
      });
      if (res.ok) {
        setShowAssign(false);
        setSelectedPio(null);
        setForm({ property_id: "", scope: "full_monitoring", notes: "" });
        fetchData();
      }
    } catch (e) { console.error(e); }
  };

  const handleUpdateStatus = async (assignmentId, status) => {
    try {
      await fetch(`${API}/pios/assignments/${assignmentId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ status }),
      });
      fetchData();
    } catch (e) { console.error(e); }
  };

  const startAssign = (pio) => {
    setSelectedPio(pio);
    setShowAssign(true);
  };

  return (
    <div data-testid="pio-management-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-serif text-2xl text-white">Property Intelligence Officers</h2>
          <p className="text-white/40 text-sm font-sans">
            {assignments.filter(a => a.status === "active").length} active assignments
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 mb-6 bg-[#11130F] border border-white/10 rounded-sm p-1 w-fit">
        {[
          { key: "available", label: "Available PIOs" },
          { key: "assignments", label: "My Assignments" },
        ].map((tab) => (
          <button
            key={tab.key}
            data-testid={`tab-${tab.key}`}
            onClick={() => setActiveTab(tab.key)}
            className={`px-4 py-2 text-xs font-sans rounded-sm transition-colors ${
              activeTab === tab.key
                ? "bg-[#D4AF37]/10 text-[#D4AF37] border border-[#D4AF37]/20"
                : "text-white/50 hover:text-white"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === "available" && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {pios.map((pio) => (
            <div key={pio.pio_id} className="bg-[#11130F] border border-white/10 rounded-sm p-5 hover:border-[#D4AF37]/40 transition-colors">
              <div className="flex items-start justify-between mb-3">
                <div className="w-12 h-12 bg-[#D4AF37]/10 border border-[#D4AF37]/20 rounded-full flex items-center justify-center">
                  <span className="text-[#D4AF37] font-serif text-lg">{pio.name.charAt(0)}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Star className="w-3 h-3 text-[#D4AF37] fill-[#D4AF37]" />
                  <span className="text-[#D4AF37] text-xs font-sans font-medium">{pio.rating}</span>
                </div>
              </div>
              <h4 className="font-serif text-base text-white mb-1">{pio.name}</h4>
              <p className="text-[#D4AF37] text-[10px] uppercase tracking-wider font-sans mb-2">{pio.specialization}</p>
              <p className="text-white/50 text-xs font-sans leading-relaxed mb-3">{pio.bio}</p>
              <div className="flex items-center gap-4 mb-4">
                <div className="flex items-center gap-1">
                  <MapPin className="w-3 h-3 text-white/30" />
                  <span className="text-white/40 text-[10px] font-sans">{pio.region}</span>
                </div>
                <span className="text-white/40 text-[10px] font-sans">{pio.experience_years}y exp</span>
                <span className="text-white/40 text-[10px] font-sans">{pio.cases_resolved} resolved</span>
              </div>
              <div className="flex gap-2">
                <Button
                  data-testid={`assign-pio-${pio.pio_id}`}
                  onClick={() => startAssign(pio)}
                  className="flex-1 bg-[#D4AF37] text-black hover:bg-[#F59E0B] rounded-sm font-sans text-xs"
                >
                  <UserPlus className="w-3 h-3 mr-1" /> Assign
                </Button>
                <Button
                  data-testid={`view-pio-${pio.pio_id}`}
                  onClick={() => { setSelectedPio(pio); setShowPioDetail(true); }}
                  variant="outline"
                  className="border-white/10 text-white/60 hover:border-[#D4AF37] hover:text-[#D4AF37] rounded-sm text-xs"
                >
                  <Eye className="w-3 h-3" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {activeTab === "assignments" && (
        <div className="space-y-3">
          {assignments.length === 0 ? (
            <div className="bg-[#11130F] border border-white/10 rounded-sm p-12 text-center">
              <Shield className="w-12 h-12 text-white/10 mx-auto mb-4" />
              <p className="text-white/40 text-sm font-sans">No PIO assignments yet. Assign a PIO to your properties for intelligence monitoring.</p>
            </div>
          ) : assignments.map((a) => (
            <div key={a.assignment_id} className="bg-[#11130F] border border-white/10 rounded-sm p-5 hover:border-[#D4AF37]/40 transition-colors">
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-[#D4AF37]/10 border border-[#D4AF37]/20 rounded-full flex items-center justify-center">
                    <span className="text-[#D4AF37] font-serif">{a.pio?.name?.charAt(0) || "P"}</span>
                  </div>
                  <div>
                    <h4 className="font-sans font-medium text-white text-sm">{a.pio?.name || "PIO"}</h4>
                    <p className="text-white/40 text-xs font-sans mt-0.5">Property: {a.property?.name || a.property_id}</p>
                    <p className="text-white/40 text-[10px] font-sans mt-0.5">Scope: {a.scope?.replace(/_/g, " ")}</p>
                    {a.notes && <p className="text-white/50 text-xs font-sans mt-1">{a.notes}</p>}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {a.status === "active" ? (
                    <>
                      <span className="bg-[#D4AF37]/10 text-[#D4AF37] text-[10px] px-2 py-0.5 rounded-sm font-sans flex items-center gap-1">
                        <Clock className="w-3 h-3" /> Active
                      </span>
                      <button
                        data-testid={`message-pio-${a.assignment_id}`}
                        onClick={() => navigate(`/dashboard/pio/chat/${a.assignment_id}`)}
                        className="bg-[#D4AF37] text-black text-[10px] px-2 py-0.5 rounded-sm font-sans flex items-center gap-1 hover:bg-[#F59E0B] transition-colors font-medium"
                      >
                        <MessageSquare className="w-3 h-3" /> Message
                      </button>
                      <button
                        data-testid={`complete-assignment-${a.assignment_id}`}
                        onClick={() => handleUpdateStatus(a.assignment_id, "completed")}
                        className="text-white/30 hover:text-[#556B2F] text-xs font-sans"
                      >
                        Mark Complete
                      </button>
                    </>
                  ) : (
                    <span className="bg-[#556B2F]/10 text-[#556B2F] text-[10px] px-2 py-0.5 rounded-sm font-sans flex items-center gap-1">
                      <CheckCircle className="w-3 h-3" /> Completed
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Assign Dialog */}
      <Dialog open={showAssign} onOpenChange={setShowAssign}>
        <DialogContent className="bg-[#11130F] border border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">
              Assign {selectedPio?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {selectedPio && (
              <div className="bg-white/5 border border-white/10 rounded-sm p-3">
                <p className="text-[#D4AF37] text-xs font-sans">{selectedPio.specialization}</p>
                <p className="text-white/40 text-[10px] font-sans mt-1">{selectedPio.region} | {selectedPio.experience_years}y exp | {selectedPio.cases_resolved} cases</p>
              </div>
            )}
            <div>
              <label className="text-white/50 text-xs font-sans mb-1 block">Property</label>
              <Select value={form.property_id} onValueChange={(v) => setForm({ ...form, property_id: v })}>
                <SelectTrigger data-testid="assign-property-select" className="bg-white/5 border-white/10 text-white rounded-sm">
                  <SelectValue placeholder="Select property" />
                </SelectTrigger>
                <SelectContent className="bg-[#11130F] border-white/10">
                  {properties.map((p) => (
                    <SelectItem key={p.property_id} value={p.property_id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-white/50 text-xs font-sans mb-1 block">Scope of Work</label>
              <Select value={form.scope} onValueChange={(v) => setForm({ ...form, scope: v })}>
                <SelectTrigger className="bg-white/5 border-white/10 text-white rounded-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#11130F] border-white/10">
                  <SelectItem value="full_monitoring">Full Monitoring</SelectItem>
                  <SelectItem value="title_verification">Title Verification</SelectItem>
                  <SelectItem value="court_tracking">Court Case Tracking</SelectItem>
                  <SelectItem value="valuation_audit">Valuation Audit</SelectItem>
                  <SelectItem value="dispute_investigation">Dispute Investigation</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-white/50 text-xs font-sans mb-1 block">Notes</label>
              <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Any specific instructions..." className="bg-white/5 border-white/10 text-white rounded-sm" />
            </div>
            <Button data-testid="submit-assign-btn" onClick={handleAssign} disabled={!form.property_id} className="w-full bg-[#D4AF37] text-black hover:bg-[#F59E0B] rounded-sm font-sans disabled:opacity-50">
              Confirm Assignment
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* PIO Detail Dialog */}
      <Dialog open={showPioDetail} onOpenChange={setShowPioDetail}>
        <DialogContent className="bg-[#11130F] border border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">{selectedPio?.name}</DialogTitle>
          </DialogHeader>
          {selectedPio && (
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-[#D4AF37]/10 border border-[#D4AF37]/20 rounded-full flex items-center justify-center">
                  <span className="text-[#D4AF37] font-serif text-2xl">{selectedPio.name.charAt(0)}</span>
                </div>
                <div>
                  <p className="text-[#D4AF37] text-xs uppercase tracking-wider font-sans">{selectedPio.specialization}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <Star className="w-3 h-3 text-[#D4AF37] fill-[#D4AF37]" />
                    <span className="text-white text-sm font-sans">{selectedPio.rating}</span>
                    <span className="text-white/30">|</span>
                    <span className="text-white/50 text-xs font-sans">{selectedPio.region}</span>
                  </div>
                </div>
              </div>
              <p className="text-white/60 text-sm font-sans leading-relaxed">{selectedPio.bio}</p>
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-white/5 border border-white/10 rounded-sm p-3 text-center">
                  <p className="font-serif text-xl text-[#D4AF37]">{selectedPio.experience_years}</p>
                  <p className="text-white/40 text-[10px] font-sans">Years Exp</p>
                </div>
                <div className="bg-white/5 border border-white/10 rounded-sm p-3 text-center">
                  <p className="font-serif text-xl text-[#D4AF37]">{selectedPio.cases_resolved}</p>
                  <p className="text-white/40 text-[10px] font-sans">Cases</p>
                </div>
                <div className="bg-white/5 border border-white/10 rounded-sm p-3 text-center">
                  <p className="font-serif text-xl text-[#D4AF37]">{selectedPio.rating}</p>
                  <p className="text-white/40 text-[10px] font-sans">Rating</p>
                </div>
              </div>
              <Button onClick={() => { setShowPioDetail(false); startAssign(selectedPio); }} className="w-full bg-[#D4AF37] text-black hover:bg-[#F59E0B] rounded-sm font-sans">
                <UserPlus className="w-4 h-4 mr-2" /> Assign to Property
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
